import {
  useRef,
  useState,
  useEffect,
} from 'react';
import type {
  ReactElement,
} from 'react';

import useOnClickOutside from 'use-onclickoutside';

import { DropdownItem } from './DropdownItem';
import type {
  Components,
  FormatOptionLabel,
  GetOptionLabel,
  OnSelect,
} from './types';

export type DropdownProps<Option> = {
  components: Components;
  getOptionLabel: GetOptionLabel<Option>;
  formatOptionLabel: FormatOptionLabel<Option>;
  options: Option[];
  onSelect: OnSelect<Option>;
  closeMenu: () => void;
};

export function Dropdown<Option>({
  components,
  getOptionLabel,
  formatOptionLabel,
  options,
  onSelect,
  closeMenu,
}: DropdownProps<Option>): ReactElement {
  const rootRef = useRef<HTMLElement>(null);
  useOnClickOutside(rootRef, closeMenu);

  const isInitRef = useRef<boolean>(true);

  const [highlightedIndex, setHighlightedIndex] = useState<number>(0);

  const highlightedIndexRef = useRef<number>(highlightedIndex);
  highlightedIndexRef.current = highlightedIndex;

  useEffect(() => {
    if (isInitRef.current) {
      isInitRef.current = false;
    } else {
      setHighlightedIndex(0);
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      switch (event.key) {
        case 'ArrowDown':
          setHighlightedIndex((prevIndex) => {
            if (prevIndex === options.length - 1) {
              return 0;
            }

            return prevIndex + 1;
          });
          break;

        case 'ArrowUp':
          setHighlightedIndex((prevIndex) => {
            if (prevIndex === 0) {
              return options.length - 1;
            }

            return prevIndex - 1;
          });
          break;

        case 'Enter':
        {
          const option = options[highlightedIndexRef.current];
          onSelect(getOptionLabel(option), option);

          break;
        }

        default:
          break;
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return (): void => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [
    options,
    getOptionLabel,
    onSelect,
  ]);

  const {
    Menu,
  } = components;

  return (
    <Menu
      ref={rootRef}
    >
      {
        options.map((option, index) => (
          <DropdownItem
            components={components}
            getOptionLabel={getOptionLabel}
            formatOptionLabel={formatOptionLabel}
            option={option}
            isHighlighted={highlightedIndex === index}
            onSelect={onSelect}
            setHighlightedIndex={setHighlightedIndex}
            index={index}
            key={index}
          />
        ))
      }
    </Menu>
  );
}
